import React, { VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { prepareProps } from 'record-it-components/utils/propUtils';

import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';
import { ConstructionSiteUtils } from '../../../classes/entities/ConstructionSite/ConstructionSiteUtils';

export type ConstructionSiteSelectProps = PropsWithoutChildren<
  Omit<
    SearchableSelectProps<ConstructionSite>,
    | 'label'
    | 'value'
    | 'items'
    | 'onValueChanged'
    | 'getDisplayTextOfItem'
    | 'getKeyOfItem'
  >
> & {
  readonly availableConstructionSites: Array<ConstructionSite>;
  readonly constructionSiteId: string | null;
  readonly onConstructionSiteChanged: (value: string | null) => void;
};

export const ConstructionSiteSelect: VoidFunctionComponent<ConstructionSiteSelectProps> =
  React.memo((props) => {
    const {
      availableConstructionSites,
      constructionSiteId,
      onConstructionSiteChanged,
      ...remainingProps
    } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { t } = useTranslation();

    const useSelectEntityItemsReturnValue = useNullableSelectEntityItems<
      ConstructionSite,
      string
    >({
      value: constructionSiteId,
      entities: availableConstructionSites,
      entityNotFoundText: t(
        'components.input.ConstructionSiteSelect.constructionSiteNotFound'
      ),
      nullValueText: t(
        'components.input.ConstructionSiteSelect.noConstructionSite'
      ),
      onValueChanged: onConstructionSiteChanged,
      getDisplayTextOfEntity: ConstructionSiteUtils.displayTextGetter,
      getKeyOfEntity: ConstructionSiteUtils.keyGetter,
      getValueOfEntity: (constructionSite) => constructionSite.id
    });

    return (
      <SearchableSelect
        label={t('components.input.ConstructionSiteSelect.label')}
        value={useSelectEntityItemsReturnValue.selectedItem}
        items={useSelectEntityItemsReturnValue.items}
        onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged}
        getDisplayTextOfItem={
          useSelectEntityItemsReturnValue.getDisplayTextOfItem
        }
        getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem}
        data-testid="ConstructionSiteSelect.SearchableSelect"
        {...remainingProps}
      ></SearchableSelect>
    );
  });
